import React, { useRef, useState, memo, useEffect } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Dropdown } from "primereact/dropdown";
import "../../../../../../../assets/css/logicsettings.css";

const XmotLogicSetting = ({
  buttonIcon: CogIcon,
  onApply,
  thresholdData,
  magnitude,
  setMagnitude,
  duration,
  setDuration,
  logicTriggerType,
  setLogicTriggerType,
  sendAfterUnit,
  sendAfterValue,
  setSendAfterUnit,
  setSendAfterValue,
  logicPanel,
  isLogicSettingsError,
}) => {
  const [errors, setErrors] = useState({});
  const [originalDuration, setOriginalDuration] = useState("");
  const [originalMagnitude, setOriginalMagnitude] = useState("");
  const [originalLogicTriggerType, setOriginalLogicTriggerType] = useState("1");
  const [originalSendAfterValue, setOriginalSendAfterValue] = useState(1);
  const [originalSendAfterUnit, setOriginalSendAfterUnit] = useState("3");
  // Initialize original values from props when component mounts or when threshold data changes
  useEffect(() => {
    if (thresholdData?.threshold) {
      const durationValue = String(
        thresholdData.threshold.usage_min_time_threshold || "",
      );
      const magnitudeValue = String(
        thresholdData.threshold.usage_xyz_threshold_magnitude || "",
      );

      setOriginalDuration(durationValue);
      setOriginalMagnitude(magnitudeValue);

      // Only set the state values if they're empty
      if (!duration) {
        setDuration(durationValue);
      }
      if (!magnitude) {
        setMagnitude(magnitudeValue);
      }
    }
  }, [thresholdData]);

  // Track current prop values as originals
  useEffect(() => {
    if (duration) {
      setOriginalDuration(String(duration));
    }
    if (magnitude) {
      setOriginalMagnitude(String(magnitude));
    }
    if (logicTriggerType) {
      setOriginalLogicTriggerType(String(logicTriggerType));
    }
    if (sendAfterValue) {
      setOriginalSendAfterValue(sendAfterValue);
    }
    if (sendAfterUnit) {
      setOriginalSendAfterUnit(String(sendAfterUnit));
    }
  }, []);

  const sendAfterValueOptions = [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
    { label: "5", value: 5 },
    { label: "6", value: 6 },
    { label: "7", value: 7 },
    { label: "8", value: 8 },
    { label: "9", value: 9 },
    { label: "10", value: 10 },
    { label: "15", value: 15 },
    { label: "30", value: 30 },
    { label: "45", value: 45 },
    { label: "60", value: 60 },
    { label: "75", value: 75 },
    { label: "90", value: 90 },
    { label: "105", value: 105 },
    { label: "120", value: 120 },
  ];

  const sendAfterUnitOptions = [
    { label: "Seconds", value: "3" },
    { label: "Minutes", value: "2" },
  ];

  // Reset values when overlay opens
  const handleOverlayOpen = (e) => {
    setErrors({});

    // Use current prop values or threshold data
    const durationValue = duration
      ? String(duration)
      : thresholdData?.threshold?.usage_min_time_threshold
        ? String(thresholdData.threshold.usage_min_time_threshold)
        : "";
    const magnitudeValue = magnitude
      ? String(magnitude)
      : thresholdData?.threshold?.usage_xyz_threshold_magnitude
        ? String(thresholdData.threshold.usage_xyz_threshold_magnitude)
        : "";

    setOriginalDuration(durationValue);
    setOriginalMagnitude(magnitudeValue);
    setOriginalLogicTriggerType(logicTriggerType || "1");
    setOriginalSendAfterValue(sendAfterValue || 1);
    setOriginalSendAfterUnit(sendAfterUnit || "3");

    setDuration(durationValue);
    setMagnitude(magnitudeValue);

    logicPanel.current.toggle(e);
  };

  const handleDurationChange = (e) => {
    const value = e.target.value;
    // Only allow digits (no decimals)
    if (value === "" || /^\d+$/.test(value)) {
      setDuration(value);
      // Clear error when user starts typing
      if (errors.duration) {
        setErrors((prev) => ({ ...prev, duration: null }));
      }
    }
  };

  const handleMagnitudeChange = (e) => {
    const value = e.target.value;
    // Allow digits and one decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setMagnitude(value);
      // Clear error when user starts typing
      if (errors.magnitude) {
        setErrors((prev) => ({ ...prev, magnitude: null }));
      }
    }
  };

  const validateFields = () => {
    const newErrors = {};

    // Convert to string and handle null/undefined
    const durationValue = duration != null ? String(duration).trim() : "";
    const magnitudeValue = magnitude != null ? String(magnitude).trim() : "";

    // Validate Duration
    if (!durationValue || durationValue === "") {
      newErrors.duration = "Duration is required";
    } else if (parseFloat(durationValue) === 0 || durationValue === "0") {
      newErrors.duration = "Duration must be greater than 0";
    } else if (durationValue.includes(".")) {
      newErrors.duration = "Duration must be a whole number";
    } else if (isNaN(parseFloat(durationValue))) {
      newErrors.duration = "Duration must be a valid number";
    }

    // Validate Magnitude
    if (!magnitudeValue || magnitudeValue === "") {
      newErrors.magnitude = "Magnitude is required";
    } else if (parseFloat(magnitudeValue) === 0 || magnitudeValue === "0") {
      newErrors.magnitude = "Magnitude must be greater than 0";
    } else if (isNaN(parseFloat(magnitudeValue))) {
      newErrors.magnitude = "Magnitude must be a valid number";
    }

    return newErrors;
  };

  const handleApply = () => {
    const validationErrors = validateFields();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Update original values after successful apply
    setOriginalDuration(String(duration));
    setOriginalMagnitude(String(magnitude));
    setOriginalLogicTriggerType(logicTriggerType);
    setOriginalSendAfterValue(sendAfterValue);
    setOriginalSendAfterUnit(sendAfterUnit);

    // Clear errors and proceed with apply
    setErrors({});
    onApply();
  };

  const handleCancel = () => {
    // Reset to original values
    setDuration(originalDuration);
    setMagnitude(originalMagnitude);
    setLogicTriggerType(originalLogicTriggerType);
    setSendAfterValue(originalSendAfterValue);
    setSendAfterUnit(originalSendAfterUnit);

    setErrors({});
    logicPanel.current.hide();
  };

  const handleClose = () => {
    // Reset to original values
    setDuration(originalDuration);
    setMagnitude(originalMagnitude);
    setLogicTriggerType(originalLogicTriggerType);
    setSendAfterValue(originalSendAfterValue);
    setSendAfterUnit(originalSendAfterUnit);

    setErrors({});
    logicPanel.current.hide();
  };

  // Get current display values - ensure they're strings
  const displayDuration = duration != null ? String(duration) : "";
  const displayMagnitude = magnitude != null ? String(magnitude) : "";

  return (
    <>
      <div className="mt-4 xmot-logic-container">
        <Button
          type="button"
          label="XMOT Logic Settings"
          icon={CogIcon ? <CogIcon /> : null}
          className="logic-settings-button logic-settings-icon"
          onClick={handleOverlayOpen}
        />

        <OverlayPanel
          ref={logicPanel}
          className="xmot-overlay-panel"
          dismissable={false}
        >
          <div className="xmot-overlay-content">
            {/* Header */}
            <div className="xmot-header">
              <h3 className="xmot-header-title">XMOT Logic Settings</h3>
              <button onClick={handleClose} className="xmot-close-button">
                ×
              </button>
            </div>

            {/* Content */}
            <div className="xmot-content">
              {/* Duration Section */}
              <div className="xmot-field-section">
                <label className="xmot-field-label">Duration:</label>
                <div className="xmot-field-input-wrapper">
                  <span>XMOT session after</span>
                  <InputText
                    value={displayDuration}
                    onChange={handleDurationChange}
                    className={`xmot-duration-input ${errors.duration ? "p-invalid" : "fs-14"}`}
                    maxLength={4}
                  />
                  <span>seconds of motion</span>
                </div>
                {errors.duration && (
                  <small className="xmot-error-message xmot-duration-error">
                    {errors.duration}
                  </small>
                )}
              </div>

              {/* Magnitude Section */}
              <div className="xmot-field-section">
                <label className="xmot-field-label">Magnitude:</label>
                <div className="xmot-field-input-wrapper">
                  <span>Minimum Magnitude</span>
                  <InputText
                    value={displayMagnitude}
                    onChange={handleMagnitudeChange}
                    className={`xmot-magnitude-input ${errors.magnitude ? "p-invalid" : ""}`}
                    maxLength={5}
                  />
                </div>
                {errors.magnitude && (
                  <small className="xmot-error-message xmot-magnitude-error">
                    {errors.magnitude}
                  </small>
                )}
              </div>

              {/* Radio Buttons Section */}
              <div className="xmot-radio-section">
                <div className="xmot-radio-wrapper">
                  <div className="xmot-radio-item">
                    <RadioButton
                      inputId="trigger1"
                      name="triggerType"
                      value="1"
                      onChange={(e) => setLogicTriggerType(e.value)}
                      checked={logicTriggerType === "1"}
                    />
                    <label htmlFor="trigger1" className="xmot-radio-label">
                      Send upon reaching trigger
                    </label>
                  </div>
                  <div className="xmot-radio-item">
                    <RadioButton
                      inputId="trigger2"
                      name="triggerType"
                      value="2"
                      onChange={(e) => setLogicTriggerType(e.value)}
                      checked={logicTriggerType === "2"}
                    />
                    <label htmlFor="trigger2" className="xmot-radio-label">
                      Send after motion ends
                    </label>
                  </div>
                </div>
              </div>

              {logicTriggerType === "2" && (
                <div className="xmot-send-after-wrapper">
                  <span>Send after</span>

                  <Dropdown
                    value={sendAfterValue}
                    onChange={(e) => setSendAfterValue(e.value)}
                    options={sendAfterValueOptions}
                    className="xmot-dropdown-value fs-14"
                  />

                  <Dropdown
                    value={sendAfterUnit}
                    onChange={(e) => setSendAfterUnit(e.value)}
                    options={sendAfterUnitOptions}
                    className="xmot-dropdown-unit fs-14"
                  />

                  <span>of motion ending</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="xmot-actions">
                <Button
                  label="Cancel"
                  onClick={handleCancel}
                  className="xmot-cancel-button"
                />
                <Button
                  label="Apply"
                  onClick={handleApply}
                  className="xmot-apply-button"
                />
              </div>
            </div>
          </div>
        </OverlayPanel>
      </div>
      {isLogicSettingsError && (
        <p className="xmot-error-message mt-2 text-start">
          Please fill the XMOT Duration and Magnitude for the XMOT Logic
          Settings.
        </p>
      )}
    </>
  );
};

export default memo(XmotLogicSetting);
